'use client';

import { useSettings } from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { usernameRegistrationStepAtom } from '../data-access/atoms/usernameRegistrationStep.atoms';

import { type RegisterSchemaType, useUserRegisterSchema } from './useAuthFormHelper';
import { useNCWTermsAndConditions } from './useNCWTermsAndConditions';
import { usePrivacyPolicy } from './usePrivacyPolicy';
import { useTermsAndConditions } from './useTermsAndConditions';
import { useUserPrefilledData } from './useUserPrefilledData';

// TODO: implement countre states once US app is ready
const useRegisterForm = (locale: string) => {
	const getTermAndConditions = useTermsAndConditions();
	const getPrivacyPolicy = usePrivacyPolicy();
	const getNCWTermsAndConditions = useNCWTermsAndConditions();
	const { isUserCountriesQueryLoading, prefilledBirthDate, prefilledCountry, prefilledEmail, prefilledUsername } =
		useUserPrefilledData();

	//const [countryStates, setCountryStates] = useState<Array<CountryState>>([]);
	const [isPolicyLoading, setPolicyLoading] = useState(true);
	const [countryTermsAndConditions, setCountryTermsAndConditions] = useState<string>('');
	const [ncwTermsAndConditions, setNcwCountryTermsAndConditions] = useState<string>('');
	const [countryPrivacyPolicy, setCountryPrivacyPolicy] = useState<string>('');
	const [UScontactUSLink, setUScontactUSLink] = useState<string>('');
	const setUsernameRegistrationStepAtom = useSetAtom(usernameRegistrationStepAtom);

	useEffect(() => {
		const handleCountryChanges = async () => {
			// Following code is commented out until US app is ready
			/*
			const statesResponse = await getCountryStates(val.id);
			statesResponse?.states && setCountryStates(statesResponse.states);
			const termsAndConditionsResponse = await getTermAndConditions(
				country.iso3 === 'USA' ? 'user_agreement_usa' : 'user_agreement'
			);
			*/

			setPolicyLoading(true);

			const [privacyPolicyResponse, termsAndConditionsResponse, ncwTermsAndConditionsResponse] =
				await Promise.all([getPrivacyPolicy(locale), getTermAndConditions(), getNCWTermsAndConditions()]);

			termsAndConditionsResponse && setCountryTermsAndConditions(termsAndConditionsResponse.link || '');
			ncwTermsAndConditionsResponse && setNcwCountryTermsAndConditions(ncwTermsAndConditionsResponse.link || '');

			if (privacyPolicyResponse) {
				setCountryPrivacyPolicy(privacyPolicyResponse.privacyPolicyLink || '');
				setUScontactUSLink(privacyPolicyResponse.contactUsLink || '');
			}

			setPolicyLoading(false);
		};

		handleCountryChanges();
	}, [getPrivacyPolicy, getTermAndConditions, locale]);

	// Handles country and state changes
	// TODO: implement call to usePostRegulation to get the kind of regulation once we get the mobile from previous screen
	// const watchCountry = registerForm.watch('country');

	// useEffect(() => {
	// 	registerForm.setValue('countryState', undefined);
	// 	registerForm.setValue('termsAndConditions', false);
	// }, [watchCountry, registerForm]);

	return {
		countryPrivacyPolicy,
		countryTermsAndConditions,
		isPolicyLoading,
		isUserCountriesQueryLoading,
		ncwTermsAndConditions,
		prefilledBirthDate,
		prefilledCountry,
		prefilledEmail,
		prefilledUsername,
		setUsernameRegistrationStepAtom,
		UScontactUSLink
	};
};

export const useNewRegisterForm = (locale: string) => {
	const {
		settings: { isUsernameStepRegistrationEnabled }
	} = useSettings();
	const {
		countryPrivacyPolicy,
		countryTermsAndConditions,
		isPolicyLoading,
		isUserCountriesQueryLoading,
		ncwTermsAndConditions,
		prefilledCountry,
		prefilledEmail,
		prefilledUsername,
		setUsernameRegistrationStepAtom,
		UScontactUSLink
	} = useRegisterForm(locale);

	const registerForm = useForm<RegisterSchemaType>({
		defaultValues: useMemo(
			() => ({
				country: prefilledCountry,
				email: prefilledEmail,
				username: prefilledUsername
			}),
			[prefilledCountry, prefilledEmail, prefilledUsername]
		),
		mode: 'onChange',
		resolver: zodResolver(useUserRegisterSchema())
	});

	const setUsernameOnForm = useCallback(
		(tmpUsername: string) => {
			registerForm.clearErrors('username');
			registerForm.setValue('username', tmpUsername);
			setUsernameRegistrationStepAtom({ suggestions: [] });
		},
		[registerForm, setUsernameRegistrationStepAtom]
	);

	const handleSuggestionClick = useCallback(
		(tmpUserName: string) => {
			setUsernameOnForm(tmpUserName);
		},
		[setUsernameOnForm]
	);

	const country = registerForm.watch('country');

	// Handles userCountry changes
	useEffect(() => {
		if (prefilledCountry) {
			const resetData: {
				country: typeof prefilledCountry;
				email: typeof prefilledEmail;
				username?: typeof prefilledUsername;
			} = {
				country: prefilledCountry,
				email: prefilledEmail
			};

			if (isUsernameStepRegistrationEnabled) {
				resetData.username = prefilledUsername;
			}

			registerForm.reset(resetData);
		}
	}, [registerForm, prefilledEmail, prefilledUsername, prefilledCountry, isUsernameStepRegistrationEnabled]);

	return {
		country,
		countryPrivacyPolicy,
		countryTermsAndConditions,
		handleSuggestionClick,
		isPolicyLoading,
		isUserCountriesQueryLoading,
		ncwTermsAndConditions,
		registerForm,
		UScontactUSLink,
		userIPCountry: prefilledCountry
	};
};
